import type { PromptsPageProps } from "./types";
import type { Prompt } from "@webuild-ai/admin/models/components";

import React, { useState } from "react";

import { SavePromptModal } from "@/components/Chat/SavePromptModal";

import { PromptsHeader } from "./components/PromptsHeader";
import { PromptsSearchBar } from "./components/PromptsSearchBar";
import { PromptsEmptyState } from "./components/PromptsEmptyState";
import { PromptsTable } from "./components/PromptsTable";
import { MobilePromptCard } from "./components/MobilePromptCard";
import { DeletePromptModal } from "./components/DeletePromptModal";
import { usePrompts } from "./hooks/usePrompts";

export const PromptsPage: React.FC<PromptsPageProps> = () => {
  const {
    prompts,
    isLoading,
    searchQuery,
    setSearchQuery,
    languageFilter,
    setLanguageFilter,
    handleEdit,
    handleDelete,
    handleCreate,
    refreshPrompts,
    editingPrompt,
    isModalOpen,
    setIsModalOpen,
  } = usePrompts();

  const [promptToDelete, setPromptToDelete] = useState<Prompt | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDeleteClick = (prompt: Prompt) => {
    setPromptToDelete(prompt);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (promptId: string) => {
    await handleDelete(promptId);
    setIsDeleteModalOpen(false);
    setPromptToDelete(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setPromptToDelete(null);
  };

  const hasPrompts = prompts.length > 0;
  const showEmptyState = isLoading || !hasPrompts;

  return (
    <div className="p-4 max-w-full" data-id="prompts-page">
      <PromptsHeader />

      <div className="p-3 sm:p-4" data-id="prompts-container">
        <PromptsSearchBar
          languageFilter={languageFilter}
          searchQuery={searchQuery}
          onCreateNew={handleCreate}
          onLanguageChange={setLanguageFilter}
          onSearchChange={setSearchQuery}
        />

        {showEmptyState ? (
          <PromptsEmptyState hasSearchQuery={!!searchQuery.trim()} isLoading={isLoading} />
        ) : (
          <>
            {/* Desktop Table View */}
            <PromptsTable prompts={prompts} onDelete={handleDeleteClick} onEdit={handleEdit} />

            {/* Tablet Table View */}
            <PromptsTable
              isTablet
              prompts={prompts}
              onDelete={handleDeleteClick}
              onEdit={handleEdit}
            />

            {/* Mobile Card View */}
            <div className="block sm:hidden" data-id="prompts-cards-mobile">
              {prompts.map((prompt) => (
                <MobilePromptCard
                  key={prompt.id}
                  prompt={prompt}
                  onDelete={handleDeleteClick}
                  onEdit={handleEdit}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <SavePromptModal
        data-id="save-prompt-modal"
        editingPrompt={editingPrompt || undefined}
        isOpen={isModalOpen}
        promptToSave={editingPrompt?.content || ""}
        onOpenChange={setIsModalOpen}
        onSuccess={refreshPrompts}
      />

      <DeletePromptModal
        isOpen={isDeleteModalOpen}
        prompt={promptToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
